/**
 * Workflow List Component
 *
 * Lists all workflows with actions.
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { MoreVertical, Edit, Trash2, Plus, Copy, Globe, User } from 'lucide-react';
import { useWorkflows, useDeleteWorkflow, useUpdateWorkflow } from '../hooks/useImageWorkflows';
import type { Workflow } from '@/types/imageWorkflow';
import { toast } from 'sonner';

// Helper to copy text to clipboard
const copyToClipboard = async (text: string, e: React.MouseEvent) => {
  e.stopPropagation();
  try {
    await navigator.clipboard.writeText(text);
    toast.success('ID copied to clipboard');
  } catch {
    toast.error('Failed to copy');
  }
};

interface WorkflowListProps {
  onSelect?: (workflow: Workflow) => void;
  onNew?: () => void;
}

export const WorkflowList: React.FC<WorkflowListProps> = ({ onSelect, onNew }) => {
  const { data, isLoading, error } = useWorkflows();
  const deleteWorkflow = useDeleteWorkflow();
  const updateWorkflow = useUpdateWorkflow();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const handleToggleSystemTemplate = async (workflow: Workflow, e: React.MouseEvent) => {
    e.stopPropagation();
    const newValue = !workflow.is_system_template;
    try {
      await updateWorkflow.mutateAsync({
        workflowId: workflow.id,
        data: { is_system_template: newValue },
      });
      toast.success(
        newValue
          ? `"${workflow.name}" is now a system template (accessible to all users)`
          : `"${workflow.name}" is now user-specific`
      );
    } catch {
      toast.error('Failed to update workflow');
    }
    setOpenMenuId(null);
  };

  const handleDelete = async (workflowId: string, name: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${name}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await deleteWorkflow.mutateAsync(workflowId);
      toast.success('Workflow deleted');
    } catch {
      toast.error('Failed to delete workflow');
    }
    setOpenMenuId(null);
  };

  const toggleMenu = (e: React.MouseEvent, workflowId: string) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === workflowId ? null : workflowId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
          <p className="text-gray-500">Loading workflows...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-red-500">
          <p>Failed to load workflows</p>
          <p className="text-sm">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  const workflows = data?.workflows || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Image Workflows</h2>
          <p className="text-sm text-gray-500">
            {workflows.length} workflow{workflows.length !== 1 ? 's' : ''}
          </p>
        </div>
        {onNew && (
          <Button variant="primary" onClick={onNew}>
            <Plus className="w-4 h-4 mr-1" />
            New Workflow
          </Button>
        )}
      </div>

      {/* Empty state */}
      {workflows.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <svg
              className="w-12 h-12 text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-700 mb-1">
              No workflows yet
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Create your first workflow to get started
            </p>
            {onNew && (
              <Button variant="secondary" onClick={onNew}>
                Create Workflow
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Workflow list */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {workflows.map((workflow) => (
          <div
            key={workflow.id}
            className="cursor-pointer"
            onClick={() => onSelect?.(workflow)}
          >
            <Card className="hover:shadow-md transition-shadow h-full">
              <div className="p-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-medium text-gray-900 truncate">
                    {workflow.name}
                  </h3>
                  {/* Workflow Key - clickable to copy */}
                  <button
                    onClick={(e) => copyToClipboard(workflow.key, e)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-mono mt-0.5 group"
                    title="Click to copy workflow key"
                  >
                    <span className="bg-blue-50 px-1.5 py-0.5 rounded">{workflow.key}</span>
                    <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  {workflow.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                      {workflow.description}
                    </p>
                  )}
                </div>

                {/* Dropdown menu */}
                <div className="relative ml-2">
                  <button
                    onClick={(e) => toggleMenu(e, workflow.id)}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>

                  {openMenuId === workflow.id && (
                    <>
                      {/* Backdrop to close menu */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(null);
                        }}
                      />
                      {/* Menu */}
                      <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-20">
                        <button
                          className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(null);
                            onSelect?.(workflow);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          onClick={(e) => handleToggleSystemTemplate(workflow, e)}
                          disabled={updateWorkflow.isPending}
                        >
                          {workflow.is_system_template ? (
                            <>
                              <User className="w-4 h-4" />
                              Make Private
                            </>
                          ) : (
                            <>
                              <Globe className="w-4 h-4" />
                              Make Public
                            </>
                          )}
                        </button>
                        <button
                          className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(workflow.id, workflow.name);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 text-xs text-gray-500 mt-3">
                <div className="flex items-center gap-1">
                  <span className="font-medium">{workflow.definition.config?.nodes?.length ?? workflow.definition.nodes?.length ?? 0}</span>
                  <span>nodes</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium">{workflow.execution_count}</span>
                  <span>runs</span>
                </div>
                <div className="flex items-center gap-1">
                  <span>v{workflow.version}</span>
                </div>
              </div>

              {/* Tags */}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {workflow.is_system_template && (
                  <span
                    className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700 flex items-center gap-1"
                    title="System template - accessible to all users"
                  >
                    <Globe className="w-3 h-3" />
                    Public
                  </span>
                )}
                <span
                  className={`px-2 py-0.5 rounded text-xs ${
                    workflow.prompt_strategy === 'agent'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-purple-100 text-purple-700'
                  }`}
                >
                  {workflow.prompt_strategy === 'agent' ? 'Agent' : 'Raw LLM'}
                </span>
                <span
                  className={`px-2 py-0.5 rounded text-xs ${
                    workflow.default_storage_mode === 'library'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {workflow.default_storage_mode === 'library' ? 'Library' : 'Temp'}
                </span>
              </div>
            </div>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkflowList;
